/**
 * Quando il microfono ti prende, e quando il motore ti capisce: sono due cose
 * diverse, e finora a schermo ce n'era una sola.
 *
 * Misurato sulle registrazioni vere di una studentessa che ha disdetto
 * (06/09/2026, blocchi da 4096 campioni a 48 kHz, cioe' esattamente quello che
 * calcola `onaudioprocess`: `recordingAmount = min(mediaDi|x| * 10, 1)`):
 *
 * | risposta | RMS       | % blocchi > 0,25 | cosa ne ha fatto il motore |
 * |----------|-----------|------------------|----------------------------|
 * | q5       | -32,5 dBFS| **0,8%**         | zero parole                |
 * | q4       | -23,2 dBFS| **48,2%**        | 500 caratteri              |
 * | q2       | -21,0 dBFS| **34,3%**        | 5,5 s di voce              |
 *
 * Lo scarto e' netto: 0,25 separa "il motore ti sente" da "non ti sente". Ma
 * NON come soglia istantanea per animare le barre — come misura SOSTENUTA.
 * Su q5 il livello istantaneo sta sotto 0,1 per 21,5 secondi su 22,1: alzare
 * la soglia delle onde non avrebbe cambiato niente li', e avrebbe spento le
 * onde a chi parla normale (q4 perdeva 9 secondi di animazione su 29).
 *
 * Quindi due numeri per due domande diverse:
 *  - SOGLIA_SEGNALE (0,02): il microfono sta prendendo qualcosa. Le onde si
 *    muovono da qui in su, cosi' chi parla piano vede che non e' rotto.
 *    Tarata fra il parlato di q5 (92,7% dei blocchi sopra) e il silenzio di
 *    q4 (sotto).
 *  - SOGLIA_UDIBILE (0,25): il motore ci puo' fare qualcosa. Sotto, si avvisa.
 *
 * RIVERIFICATO SU QUARANTA VOCI (07/09/2026)
 *
 * Tre risposte di una sola studentessa sono una taratura fragile: microfono
 * suo, stanza sua. Ripreso il conto su **40 risposte vere di 40 studenti
 * diversi** (una per studente, la piu' recente, dal 15/07 al 07/09), stesso
 * inviluppo e stessa macchina a stati di qui sotto:
 *
 *  - l'avviso scatta su **1 sola su 40**, ed e' il caso per cui e' nato
 *    (mediana 0,031, sopra soglia lo 0,8% del tempo). Zero falsi allarmi.
 *  - **non e' una soglia in bilico**: portandola da 0,15 a 0,40 — un fattore
 *    quasi tre — scatta sempre e solo quella. Il numero 0,25 non e' delicato.
 *  - fra chi parla, la piu' vicina al bordo ha 768 ms di voce udibile contro
 *    i 500 che bastano: margine 1,5x. La successiva sta a 3,1x.
 *
 * ⚠️ Quello che l'avviso NON copre, e si e' visto solo adesso: **2 risposte su
 * 40 (5%) sono praticamente mute** — segnale sopra 0,02 nell'1,6% e nell'1,9%
 * dei blocchi, per 11 e 18 secondi. Non prendono l'avviso di proposito: sotto
 * SOGLIA_SEGNALE si e' deciso che e' silenzio, e chi sta pensando non deve
 * ritrovarsi un rimprovero addosso. Ma un microfono vivo in una stanza vera
 * non sta a 0,003: quelle non sono persone che pensano, e' roba che non
 * arriva. Serve un messaggio DIVERSO da questo ("dal microfono non arriva
 * niente"), non un allargamento di questo. Non l'ho scritto: e' una decisione
 * di prodotto, non una soglia da spostare.
 */

/** Sotto questo livello non sta arrivando niente: e' silenzio, non voce debole. */
export const SOGLIA_SEGNALE = 0.02;

/** Sopra questo livello la trascrizione funziona. Sotto, no. */
export const SOGLIA_UDIBILE = 0.25;

/**
 * Quanto segnale accumulare prima di dire qualcosa. A 6 secondi il margine e'
 * 3x: quando q5 arriva qui ha 0 ms di voce udibile, q4 ne ha 1536.
 * (A 5 secondi q4 ne aveva 597, troppo vicino ai 500: chi parte piano si
 * sarebbe preso l'avviso per sbaglio.)
 */
export const MS_SEGNALE_PRIMA_DELL_AVVISO = 6000;

/** Mezzo secondo di voce vera basta a dire che il microfono e' a posto. */
export const MS_UDIBILE_CHE_BASTA = 500;

export const ascoltoIniziale = () => ({
  msSegnale: 0,
  msUdibile: 0,
  avviso: false,
});

/**
 * Un blocco audio alla volta. Puro: nessun timer, nessuno stato nascosto.
 *
 * @param {{msSegnale:number,msUdibile:number,avviso:boolean}} stato
 * @param {number} livello  `recordingAmount`, 0..1
 * @param {number} ms       durata del blocco
 */
export const aggiornaAscolto = (stato, livello, ms) => {
  if (livello >= SOGLIA_UDIBILE) {
    const msUdibile = stato.msUdibile + ms;
    return {
      msSegnale: stato.msSegnale + ms,
      msUdibile,
      // L'avviso non si spegne al primo blocco fortunato: ci vuole mezzo
      // secondo di voce vera, altrimenti lampeggerebbe a ogni sillaba forte.
      avviso: msUdibile < MS_UDIBILE_CHE_BASTA ? stato.avviso : false,
    };
  }

  // Silenzio: non conta né a favore né contro. Chi sta pensando non deve
  // ritrovarsi un avviso addosso.
  if (livello < SOGLIA_SEGNALE) return stato;

  const msSegnale = stato.msSegnale + ms;
  return {
    msSegnale,
    msUdibile: stato.msUdibile,
    avviso:
      stato.avviso ||
      (msSegnale >= MS_SEGNALE_PRIMA_DELL_AVVISO &&
        stato.msUdibile < MS_UDIBILE_CHE_BASTA),
  };
};

export default aggiornaAscolto;
